import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { forkJoin, of } from 'rxjs';
import { finalize, take, tap } from 'rxjs/operators';
import { ToastService } from 'src/app/shared/services/toast.service';
import { SpinnerService } from 'src/app/shared/spinner/spinner.service';

import { BuyerService } from '../../../core/api/buyer.service';
import { CohortService } from '../../../core/api/cohort.service';
import { RegionService } from '../../../core/api/region.service';
import { FormatHelperService } from '../../../core/helper/format-helper.service';
import { Buyer, Cohort } from '../../../shared/models';
import { DocusignTemplate } from '../../../shared/models/docusign-template.model';
import * as fromApp from '../../../store';
import { DocumentService } from "../../../core/api/document.service";
import { WelcomeDocument } from "../../../shared/models/welcome-document.model";
import { selectCurrentUser } from "../../../store/auth/auth.selectors";

@Component({
  selector: 'fc-edit-cohort-page',
  templateUrl: './edit-cohort-page.component.html',
  styleUrls: ['./edit-cohort-page.component.scss'],
})
export class EditCohortPageComponent implements OnInit {
  destroyRef = inject(DestroyRef);

  @ViewChild('welcomeDocFileUpload', { static: false })
  welcomeDocFileUpload: ElementRef;

  id: number = null;
  cohort: Cohort = null;
  welcomeDocs: WelcomeDocument[] = [];
  cohortForm: UntypedFormGroup;
  regions: unknown;
  buyers: Buyer[];
  templates: DocusignTemplate[] = [];

  isSuperAdmin = false;
  originalCohortId: number = null;

  constructor(
    readonly regionService: RegionService,
    readonly buyerService: BuyerService,
    readonly store: Store<fromApp.AppState>,
    readonly spinnerService: SpinnerService,
    readonly formatHelper: FormatHelperService,
    readonly documentService: DocumentService,
    readonly cohortService: CohortService,
    readonly formBuilder: UntypedFormBuilder,
    readonly router: Router,
    readonly route: ActivatedRoute,
    readonly toastService: ToastService
  ) {}

  firstDateFilter = (d: Date | null): boolean => {
    const day = (d || new Date()).getDate();
    return day === 1;
  };

  endDateFilter = (d: Date | null): boolean => {
    const day = d || new Date();
    const lastDay = new Date(day.getFullYear(), day.getMonth() + 1, 0).getDate();
    return day.getDate() === lastDay;
  };

  ngOnInit() {
    this.id = +this.route.snapshot.params.id;

    this.store
      .select(selectCurrentUser)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(currentUser => {
        this.isSuperAdmin = currentUser.isSuperAdmin;
      });

    forkJoin([
      this.buyerService.getAllBuyers(),
      this.regionService.getAllRegions(),
      this.id ? this.cohortService.getCohort(this.id, false) : of(null)
    ])
      .pipe(
        tap(([buyers, regions, cohort]) => {
          this.buyers = buyers;
          this.regions = regions;
          this.cohort = cohort;
          if (this.cohort) {
            this.updateDocusignTemplates({ value: this.cohort.buyer.id } as MatSelectChange);
            this.welcomeDocs = cohort.welcomeDocuments.sort((a, b) => a.name.localeCompare(b.name));
          }
        }, takeUntilDestroyed(this.destroyRef))
      )
      .subscribe(
        () => {
          this.createEditCohortForm();
          if (this.cohort) {
            // set values
            this.cohortForm.get('id').setValue(this.cohort.id);
            this.cohortForm.get('name').setValue(this.cohort.name);
            this.cohortForm.get('region').setValue(this.cohort.region.id);
            this.cohortForm.get('methodology').setValue(this.cohort.methodology);
            this.cohortForm.get('startDateForm').setValue(`${this.cohort.startDate}T00:00:00`);
            this.cohortForm.get('enrollmentStartDateForm').setValue(`${this.cohort.enrollmentStartDate}T00:00:00`);
            this.cohortForm.get('enrollmentEndDateForm').setValue(`${this.cohort.enrollmentEndDate}T00:00:00`);
            this.cohortForm.get('buyer').setValue(this.cohort.buyer.id);
            this.cohortForm.get('acrProjectName').setValue(this.cohort.acrProjectName);
            this.cohortForm.get('payPeriodCadence').setValue(this.cohort.payPeriodCadence);
            this.cohortForm.get('internalBufferValue').setValue(this.cohort.internalBufferValue);
            this.cohortForm.get('marketPricePercentage').setValue(this.cohort.marketPricePercentage);
            this.cohortForm.get('pricePerTon').setValue(this.formatHelper.formatCurrency(this.cohort.pricePerTon));
            this.cohortForm.get('minimumErtPerAcre').setValue(this.cohort.minimumErtPerAcre);
            this.cohortForm
              .get('minimumPaymentPerAcre')
              .setValue(this.formatHelper.formatCurrency(this.cohort.minimumPaymentPerAcre));
            this.cohortForm.get('initialFixedYears').setValue(this.cohort.initialFixedYears);
            this.cohortForm.get('termsUrl').setValue(this.cohort.termsUrl);
            this.cohortForm.get('terms1').setValue(this.cohort.terms1);
            this.cohortForm.get('terms2').setValue(this.cohort.terms2);
            this.cohortForm.get('terms3').setValue(this.cohort.terms3);
            this.cohortForm.get('bonusAmount').setValue(this.formatHelper.formatCurrency(this.cohort.bonusAmount || 0));
            this.cohortForm.get('bonusLength').setValue(this.cohort.bonusLength || 0);
            this.cohortForm.get('bonusIsPpt').setValue(this.cohort.bonusIsPpt);
            this.cohortForm.get('docusignOneTime').setValue(this.cohort.docusignOneTime);
            this.cohortForm.get('docusignTemplateId').setValue(this.cohort.docusignTemplateId);

            // disable controls that shouldn't be editable
            this.cohortForm.controls.region.disable();
            this.cohortForm.controls.methodology.disable();
            this.cohortForm.controls.enrollmentStartDateForm.disable();
            this.cohortForm.controls.startDateForm.disable();
            this.cohortForm.controls.buyer.disable();
            this.cohortForm.controls.payPeriodCadence.disable();

            if (!this.isSuperAdmin) {
              this.cohortForm.disable();
            }
          }
        },
      );
  }

  createEditCohortForm(): void {
    this.cohortForm = this.formBuilder.group({
      acrProjectName: '',
      bonusAmount: [this.formatHelper.formatCurrency(0.0), Validators.required],
      bonusIsPpt: false,
      bonusLength: 0,
      buyer: ['', Validators.required],
      docusignOneTime: false,
      docusignTemplateId: '',
      enrollmentEndDateForm: ['', Validators.required],
      enrollmentStartDateForm: ['', Validators.required],
      id: '',
      initialFixedYears: [20, Validators.required],
      internalBufferValue: [0.0, [Validators.required, Validators.pattern(/^(?:0*(?:\.\d+)?|1(\.0*)?)$/)]],
      marketPricePercentage: [0.0, [Validators.required, Validators.pattern(/^(?:0*(?:\.\d+)?|1(\.0*)?)$/)]],
      methodology: ['SNIPFL', Validators.required],
      minimumErtPerAcre: [0.0, Validators.required],
      minimumPaymentPerAcre: [this.formatHelper.formatCurrency(0.0), Validators.required],
      name: ['', Validators.required],
      payPeriodCadence: [12, Validators.required],
      pricePerTon: [this.formatHelper.formatCurrency(1.0), Validators.required],
      region: ['', Validators.required],
      startDateForm: ['', Validators.required],
      terms1: '',
      terms2: '',
      terms3: '',
      termsUrl: '',
    });
  }

  onSubmit(): void {
    const pricePerTon = this.formatHelper.removeCurrency(this.cohortForm.value.pricePerTon);
    const minimumPaymentPerAcre = this.formatHelper.removeCurrency(this.cohortForm.value.minimumPaymentPerAcre);
    const cohort = new Cohort({
      name: this.cohortForm.value.name,
      startDate: new Date(this.cohortForm.controls.startDateForm.value).getTime() / 1000,
      enrollmentStartDate: new Date(this.cohortForm.controls.enrollmentStartDateForm.value).getTime() / 1000,
      enrollmentEndDate: new Date(this.cohortForm.controls.enrollmentEndDateForm.value).getTime() / 1000,
      regionId: this.cohortForm.controls.region.value,
      buyerId: this.cohortForm.controls.buyer.value,
      payPeriodCadence: this.cohortForm.controls.payPeriodCadence.value,
      internalBufferValue: this.cohortForm.value.internalBufferValue,
      marketPricePercentage: this.cohortForm.value.marketPricePercentage,
      pricePerTon: pricePerTon,
      methodology: this.cohortForm.value.methodology,
      minimumPaymentPerAcre: minimumPaymentPerAcre,
      minimumErtPerAcre: this.cohortForm.value.minimumErtPerAcre,
      initialFixedYears: this.cohortForm.controls.initialFixedYears.value,
      termsUrl: this.cohortForm.value.termsUrl,
      terms1: this.cohortForm.value.terms1,
      terms2: this.cohortForm.value.terms2,
      terms3: this.cohortForm.value.terms3,
      acrProjectName: this.cohortForm.value.acrProjectName,
      bonusAmount: this.formatHelper.removeCurrency(this.cohortForm.value.bonusAmount),
      bonusLength: this.cohortForm.value.bonusLength,
      bonusIsPpt: this.cohortForm.value.bonusIsPpt,
      docusignOneTime: this.cohortForm.value.docusignOneTime,
      docusignTemplateId: this.cohortForm.value.docusignTemplateId ? this.cohortForm.value.docusignTemplateId : null,
      id: this.cohortForm.value.id ? this.cohortForm.value.id : null,
    });

    this.spinnerService.show('app-spinner');
    let functionCall = this.cohortService.createCohort(cohort, this.originalCohortId);
    if (cohort.id) {
      functionCall = this.cohortService.updateCohort(cohort);
    }
    functionCall.pipe(finalize(() => this.spinnerService.hide('app-spinner')))
      .subscribe(
        () => {
          this.cohortForm.markAsPristine();
          this.router.navigateByUrl('/admin/manage/cohort');
        },
      );
  }

  uploadWelcomeDoc() {
    const welcomeDocFileUpload = this.welcomeDocFileUpload.nativeElement;
    welcomeDocFileUpload.onchange = () => {
      if (welcomeDocFileUpload.files.length > 0) {
        for (let x = 0; x < welcomeDocFileUpload.files.length; x++) {
          this.documentService.uploadWelcomeDoc(this.id, null, welcomeDocFileUpload.files[x])
            .then((docs: WelcomeDocument[]) => {
              this.welcomeDocs = docs.sort((a, b) => a.name.localeCompare(b.name));
            });
        }
      }
    };
    welcomeDocFileUpload.click();
  }

  removeWelcomeDoc(docId: number) {
    this.documentService.deleteWelcomeDoc(docId).subscribe((remainingDocs: WelcomeDocument[]) => {
      this.welcomeDocs = remainingDocs.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  get isFormDirty(): boolean {
    return this.cohortForm.dirty;
  }

  cloneCohort() {
    this.originalCohortId = this.cohort.id;
    this.cohort = null;
    this.cohortForm.controls.region.enable();
    this.cohortForm.controls.methodology.enable();
    this.cohortForm.controls.enrollmentStartDateForm.enable();
    this.cohortForm.controls.startDateForm.enable();
    this.cohortForm.controls.buyer.enable();
    this.cohortForm.controls.payPeriodCadence.enable();
    this.cohortForm.get('id').setValue(null);
    this.cohortForm.get('name').setValue(this.cohortForm.value.name + '-Cloned');
  }

  updateDocusignTemplates($event: MatSelectChange) {
    this.buyerService.getEdocTemplates(`?buyer_id=${$event.value}`).subscribe(templates => {
      this.templates = templates;
    });
  }

  downloadDocument(element: WelcomeDocument) {
    this.documentService
      .downloadWelcomeDocument(element.id)
      .then(uri => window.open(uri, '_blank'));
  }

  toggleContract(welcomeDocumentId: number) {
    this.documentService.markDocumentAsContract(this.cohort.id, welcomeDocumentId)
      .subscribe((remainingDocs: WelcomeDocument[]) => {
        this.welcomeDocs = remainingDocs.sort((a, b) => a.name.localeCompare(b.name));
      });

  }
}
